import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { MessageSquare } from "lucide-react";
import { API } from "./shared";

// ---------- In-platform chat (buyer <-> supplier/agency) ----------
export function ChatPanel({ scopeType, scopeId, role }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(true);
  const scrollRef = useRef(null);
  const canSend = role === "buyer" || role === "agency" || role === "printer";

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/chats/${scopeType}/${scopeId}/messages`);
      setMessages(data.messages || []);
    } catch (e) { /* ignore 403 */ }
  };

  useEffect(() => {
    load();
    // Open a WebSocket for push updates; keep a 20s poll as a fallback for
    // flaky networks / proxies that drop the socket.
    const wsBase = (API || "").replace(/^http/, "ws");
    let ws = null;
    try {
      ws = new WebSocket(`${wsBase}/chat/${scopeType}/${scopeId}/ws`);
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data?.type === "message" && data.message?.id) {
            setMessages(prev => prev.some(m => m.id === data.message.id) ? prev : [...prev, data.message]);
          }
        } catch { /* ignore malformed */ }
      };
      ws.onerror = () => { /* fall back silently to polling */ };
    } catch { /* ignore WS unsupported */ }
    const t = setInterval(load, 20000);
    return () => { clearInterval(t); try { ws && ws.close(); } catch {} };
    // eslint-disable-next-line
  }, [scopeType, scopeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true); setErr("");
    try {
      await axios.post(`${API}/chats/${scopeType}/${scopeId}/messages`, { text: text.trim() });
      setText("");
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send message.");
    } finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }); }
    catch { return ""; }
  };

  return (
    <div data-testid="chat-panel" style={{marginTop:20,border:"1px solid #E5E7EB",borderRadius:12,background:"#fff",overflow:"hidden"}}>
      <button
        type="button"
        data-testid="chat-toggle"
        onClick={() => setOpen(o => !o)}
        style={{width:"100%",background:"#F9FAFB",border:"none",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}
      >
        <span style={{display:"inline-flex",alignItems:"center",gap:8,color:"#111827",fontWeight:600,fontSize:13}}>
          <MessageSquare size={15}/> Secure in-platform chat {messages.length > 0 ? <span style={{background:"#2563EB",color:"#fff",padding:"1px 8px",borderRadius:999,fontSize:10,fontWeight:700}}>{messages.length}</span> : null}
        </span>
        <span style={{fontSize:11,color:"#6B7280",fontWeight:500}}>{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div style={{padding:"12px 16px"}}>
          <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",color:"#92400E",padding:"8px 12px",borderRadius:8,fontSize:11,marginBottom:10}}>
            All communication must stay on GoPrintPack. Sharing email, phone or external links is blocked and will not be delivered.
          </div>
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            style={{maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:8,padding:"4px 2px"}}
          >
            {messages.length === 0 ? (
              <p style={{color:"#9CA3AF",fontSize:12,textAlign:"center",padding:"20px 0"}}>No messages yet. Start the conversation below.</p>
            ) : messages.map(m => {
              const isSystem = m.sender_role === "system" || m.system;
              const isAdmin = m.sender_role === "admin";
              const align = (m.sender_role === "buyer") ? "flex-start" : "flex-end";
              const bg = isSystem ? "#F0FDF4" : isAdmin ? "#EFF6FF" : (m.sender_role === "buyer" ? "#F3F4F6" : "#111827");
              const color = isSystem ? "#065F46" : isAdmin ? "#1E3A8A" : (m.sender_role === "buyer" ? "#111827" : "#F9FAFB");
              return (
                <div key={m.id} style={{alignSelf: isSystem ? "center" : align, maxWidth: isSystem ? "100%" : "80%"}}>
                  {!isSystem && <div style={{fontSize:10,color:"#6B7280",marginBottom:3, textAlign: align === "flex-end" ? "right" : "left"}}><b>{m.sender_name}</b> · {fmtTime(m.created_at)}</div>}
                  <div style={{background:bg,color,padding: isSystem ? "6px 12px" : "8px 12px",borderRadius: isSystem ? 999 : 10, fontSize: isSystem ? 11 : 13, fontWeight: isSystem ? 600 : 400, whiteSpace:"pre-wrap", wordBreak:"break-word", border: isSystem ? "1px dashed #86EFAC" : "none", display:"inline-block"}}>
                    {isSystem && <span style={{marginRight:6}}>·</span>}{m.text}
                  </div>
                </div>
              );
            })}
          </div>
          {canSend && (
            <form onSubmit={send} style={{marginTop:12,display:"flex",gap:8,alignItems:"flex-end"}}>
              <textarea
                data-testid="chat-input"
                value={text}
                onChange={e => setText(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={role === "buyer" ? "Ask the agency anything about the artwork…" : "Reply to the buyer…"}
                style={{flex:1,padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,fontFamily:"inherit",resize:"vertical"}}
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={sending || !text.trim()}
                style={{background:"#111827",color:"#FACC15",border:"none",padding:"10px 18px",borderRadius:8,fontSize:12,fontWeight:700,cursor:(sending || !text.trim()) ? "not-allowed" : "pointer",opacity:(sending || !text.trim()) ? 0.5 : 1}}
              >{sending ? "Sending…" : "Send"}</button>
            </form>
          )}
          {err && <div data-testid="chat-error" style={{marginTop:8,background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12}}>{err}</div>}
          {role === "admin" && <p style={{marginTop:8,fontSize:11,color:"#6B7280"}}>Admin read-only view · you cannot post on behalf of buyer or agency.</p>}
        </div>
      )}
    </div>
  );
}
